import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Check } from "lucide-react";

// Public DSS AI plans grid + registration form (landing page). Plans come live from
// /api/plans so the admin's pricing edits appear instantly.
export default function PlansRegister() {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    api.get("/plans").then((r) => {
      const active = (r.data || []).filter((p) => p.active);
      setPlans(active);
      if (active.length) setPlanId(active[0].id);
    }).catch(() => {});
  }, []);

  if (!plans.length) return null;

  const pick = (id) => {
    setPlanId(id);
    setDone(false);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/register", { name, email, mobile, plan_id: planId, message });
      toast.success(data.message || "Registration received");
      setDone(true);
      setName(""); setEmail(""); setMobile(""); setMessage("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-cyan-600 focus:ring-2 focus:ring-cyan-500/20 text-sm transition-all";
  const labelCls = "block text-xs uppercase font-semibold text-slate-500 code-font mb-2 tracking-wider";

  return (
    <section id="plans" className="py-24 px-6 bg-slate-950 border-b border-slate-800" data-testid="plans-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <span className="text-xs uppercase font-bold tracking-widest text-cyan-400 code-font">DSS AI Plans</span>
          <h2 className="mt-2 text-3xl font-bold text-white tracking-tight">Students, creators & enterprises welcome</h2>
          <p className="mt-3 text-sm text-slate-400">Pick a plan and register — our team approves every account personally, usually the same day.</p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`premium-card accent-bar relative overflow-hidden p-6 rounded-xl border transition-all ${
                planId === p.id
                  ? "bg-slate-900 border-cyan-400/60 shadow-[0_0_30px_-8px_rgba(0,240,255,0.4)]"
                  : "bg-slate-900/70 border-slate-700/60 hover:border-cyan-500/40"
              }`}
              data-testid={`plan-card-${p.id}`}
            >
              <h3 className="text-lg font-bold text-white">{p.name}</h3>
              {p.tagline && <p className="mt-1 text-xs text-slate-400">{p.tagline}</p>}
              <div className="mt-4 text-3xl font-black neon-text" data-testid={`plan-price-${p.id}`}>{p.price}</div>
              {p.features?.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 mt-0.5 text-cyan-400 shrink-0" aria-hidden="true" />
                      {f}
                    </li>
                  ))}
                </ul>
              )}
              <button
                onClick={() => pick(p.id)}
                className="mt-6 w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-sky-500 text-slate-950 font-bold uppercase text-xs tracking-wider hover:from-cyan-400 hover:to-sky-400 shadow-lg shadow-cyan-500/20 transition-all"
                data-testid={`plan-register-${p.id}`}
              >
                Register — {p.name}
              </button>
            </div>
          ))}
        </div>

        <div ref={formRef} className="mt-14 max-w-2xl mx-auto">
          <form onSubmit={submit} className="accent-bar relative overflow-hidden p-8 rounded-2xl bg-slate-900/80 backdrop-blur border border-slate-700/60 space-y-5" data-testid="register-form">
            <div className="flex items-center justify-between pb-3 border-b border-slate-700/60 text-xs code-font text-slate-400">
              <span>new-member.registration</span>
              <span className="text-cyan-400">ADMIN APPROVAL REQUIRED</span>
            </div>
            {done ? (
              <div className="text-center py-6" data-testid="register-success">
                <div className="text-2xl font-bold text-white">Registration sent</div>
                <p className="mt-2 text-sm text-slate-400">
                  Our team will review your {plans.find((p) => p.id === planId)?.name || ""} plan request and approve it shortly.
                  You will receive your login details from us directly.
                </p>
                <button type="button" onClick={() => setDone(false)} className="mt-4 text-xs code-font text-cyan-400 hover:text-cyan-300" data-testid="register-again">
                  Register another person &rarr;
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls} htmlFor="reg-name">Full name</label>
                    <input id="reg-name" data-testid="register-name-input" required className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="reg-mobile">Mobile</label>
                    <input id="reg-mobile" data-testid="register-mobile-input" required className={inputCls} value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" inputMode="numeric" />
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="reg-email">Email</label>
                  <input id="reg-email" data-testid="register-email-input" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="reg-plan">Plan</label>
                  <select id="reg-plan" data-testid="register-plan-select" className={inputCls} value={planId} onChange={(e) => setPlanId(e.target.value)}>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} — {p.price}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="reg-message">Anything we should know? (optional)</label>
                  <textarea id="reg-message" data-testid="register-message-input" rows={3} className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="College / company, what you want to build…" />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full px-6 py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-sky-500 text-slate-950 font-bold uppercase text-xs tracking-wider hover:from-cyan-400 hover:to-sky-400 shadow-lg shadow-cyan-500/20 transition-all disabled:opacity-60"
                  data-testid="register-submit-button"
                >
                  {sending ? "Sending…" : "Send Registration"}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
